import { useEffect, useState } from 'react';
import { updateProfile, useCurrentUserProfile } from '@busy/api';
import { useToast } from '@busy/ui';
import { useT } from '../../lib/i18n';

// Settings → Notifications panel.
// Persists user notification preferences to users.notification_prefs (jsonb).
// Onboarding step 4 sets the initial values; this lets the user edit them
// later. The toggles persist immediately on save — actual push delivery
// lands with the iOS/Android shells in v1.1.

type Prefs = {
  dailyEmail: boolean;
  push: boolean;
  weekly: boolean;
  atrisk: boolean;
};

const DEFAULT_PREFS: Prefs = {
  dailyEmail: true,
  push: true,
  weekly: true,
  atrisk: true,
};

function readPrefs(raw: unknown): Prefs {
  const p = (raw ?? {}) as Partial<Prefs>;
  return {
    dailyEmail: p.dailyEmail ?? DEFAULT_PREFS.dailyEmail,
    push: p.push ?? DEFAULT_PREFS.push,
    weekly: p.weekly ?? DEFAULT_PREFS.weekly,
    atrisk: p.atrisk ?? DEFAULT_PREFS.atrisk,
  };
}

export function NotificationsPanel() {
  const t = useT();
  const toast = useToast();
  const profile = useCurrentUserProfile();
  const [prefs, setPrefs] = useState<Prefs>(DEFAULT_PREFS);
  const [saving, setSaving] = useState(false);

  // Hydrate from profile when it loads
  useEffect(() => {
    if (profile.data) setPrefs(readPrefs(profile.data.notification_prefs));
  }, [profile.data]);

  function set(key: keyof Prefs, val: boolean) {
    setPrefs((p) => ({ ...p, [key]: val }));
  }

  async function handleSave() {
    setSaving(true);
    // Merge with whatever else might be in notification_prefs (time/days were
    // also stashed there during onboarding). Read latest first to avoid
    // clobbering keys we don't manage on this panel.
    const existing = (profile.data?.notification_prefs ?? {}) as Record<string, unknown>;
    const merged = { ...existing, ...prefs };
    const { error } = await updateProfile({ notification_prefs: merged });
    setSaving(false);
    if (error) {
      toast(t('settings.profile.saveError', { message: error.message }), 'error');
    } else {
      toast(t('settings.notif.saved'), 'success');
    }
  }

  return (
    <div className="space-y-3">
      <ToggleRow
        title={t('settings.notif.dailyEmail')}
        sub={t('settings.notif.dailyEmailSub')}
        checked={prefs.dailyEmail}
        onChange={(v) => set('dailyEmail', v)}
      />
      <ToggleRow
        title={t('settings.notif.push')}
        sub={t('settings.notif.pushSub')}
        checked={prefs.push}
        onChange={(v) => set('push', v)}
      />
      <ToggleRow
        title={t('settings.notif.weekly')}
        sub={t('settings.notif.weeklySub')}
        checked={prefs.weekly}
        onChange={(v) => set('weekly', v)}
      />
      <ToggleRow
        title={t('settings.notif.atrisk')}
        sub={t('settings.notif.atriskSub')}
        checked={prefs.atrisk}
        onChange={(v) => set('atrisk', v)}
      />

      <p className="text-[11px] text-ink-500 leading-snug pt-2">
        {t('settings.notif.deliveryNote')}
      </p>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving || profile.loading}
        className="w-full h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
      >
        {saving ? t('settings.profile.saving') : t('settings.notif.save')}
      </button>
    </div>
  );
}

function ToggleRow({
  title,
  sub,
  checked,
  onChange,
}: {
  title: string;
  sub: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-surface px-4 py-3 cursor-pointer">
      <div className="min-w-0 flex-1">
        <strong className="block text-sm">{title}</strong>
        <span className="text-xs text-ink-500">{sub}</span>
      </div>
      <span
        className={`relative inline-block w-10 h-6 rounded-full transition-colors shrink-0 ${
          checked ? 'bg-navy-900 dark:bg-gold-500' : 'bg-line-strong'
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="absolute opacity-0 w-full h-full cursor-pointer"
        />
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-4' : ''
          }`}
        />
      </span>
    </label>
  );
}
